/* eslint-disable @typescript-eslint/no-namespace */
import * as dayjs from 'dayjs';
import { TicketTemplateComponent } from '../components/events/ticket-template-component.js';
import { EventProfile } from '../models/serializable/event-profile.js';
import { EventPrivacyMode } from '../models/serializable/event-privacy-mode.js';
import { EventPrivacySettingsComponent } from '../components/events/event-privacy-settings-component.js';
import { LocalizedString } from '../models/serializable/localized-string.js';
import { LocalizedNameComponent } from '../components/point-of-interest/localized-name-component.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { DateSpanComponent } from '../components/misc/date-span-component.js';
import { ProfilePhotosComponent } from '../components/misc/profile-photos-component.js';
import { PlaceDisplayMode } from '../models/serializable/place.js';
import { LocationComponent } from '../components/events/location-component.js';
import { EventCategory } from '../models/serializable/event-category.js';
import { EventClassificationComponent, EventPriority } from '../components/events/event-classification-component.js';
import { DateFormatComponent, EventDateFormat } from '../components/events/date-format-component.js';
import { PassEventOverrideIntentComponent } from '../components/events/pass-event-override-intent-component.js';
import { LocalizedRichDescription } from '../models/serializable/localized-rich-description.js';
import { LocalizedRichDescriptionComponent } from '../components/business/localized-rich-description-component.js';
import { Cardinal } from '../models/core/cardinal.js';
import { findAllSeatsOfSeatingLayout } from './seating-layout-utils.js';
import { SeatingStateHelper } from './seating-state-helper.js';
import { SeatingLayout } from '../models/serializable/seating/seating-layout.js';
import { EventSeatingState } from '../models/serializable/seating-state/event-seating-state.js';
import { EventSeatSelectionLock } from '../models/serializable/ticketing-state/event-seat-selection-lock.js';
import { SeatState } from '../models/serializable/seating-state/seat-state.js';
import { EventTicket, EventTicketStatus } from '../models/serializable/event-ticket.js';
import { SeatingSettingsComponent } from '../components/events/seating-settings-component.js';
import { ThirdPartyEventComponent } from '../components/events/third-party-event-component.js';
import { PhotoReference } from '../data-models.js';

export namespace EventUtils {
  export function findPrivacyMode(eventProfile: EventProfile): EventPrivacyMode | null {
    const eventPrivacyComponent = eventProfile.findComponentByTypeId<EventPrivacySettingsComponent>(
        EventPrivacySettingsComponent.TYPE_ID,
    );

    return eventPrivacyComponent?.privacyMode ?? null;
  }

  export function findName(eventProfile: EventProfile): LocalizedString | null {
    const localizedNameComponent = eventProfile.findComponentByTypeId<LocalizedNameComponent>(LocalizedNameComponent.TYPE_ID);
    if(isNullOrUndefined(localizedNameComponent)) {
      return null;
    }

    return localizedNameComponent.names;
  }

  export function findDescription(eventProfile: EventProfile): LocalizedRichDescription | null {
    const localizedRichDescriptionComponent = eventProfile.findComponentByTypeId<LocalizedRichDescriptionComponent>(LocalizedRichDescriptionComponent.TYPE_ID);
    if(isNullOrUndefined(localizedRichDescriptionComponent)) {
      return null;
    }

    return localizedRichDescriptionComponent.descriptions;
  }

  export function findStartDate(eventProfile: EventProfile): dayjs.Dayjs | null {
    const dateSpanComponent = eventProfile.findComponentByTypeId<DateSpanComponent>(DateSpanComponent.TYPE_ID);

    return dateSpanComponent?.dateSpan?.from ?? null;
  }

  export function findEndDate(eventProfile: EventProfile): dayjs.Dayjs | null {
    const dateSpanComponent = eventProfile.findComponentByTypeId<DateSpanComponent>(DateSpanComponent.TYPE_ID);

    return dateSpanComponent?.dateSpan?.to ?? null;
  }

  export function findPhotoReferences(eventProfile: EventProfile): PhotoReference[] | null {
    const profilePhotosComponent = eventProfile.findComponentByTypeId<ProfilePhotosComponent>(
        ProfilePhotosComponent.TYPE_ID,
    );

    if(isNullOrUndefined(profilePhotosComponent)) {
      return null;
    }

    return profilePhotosComponent.portfolio.photos;
  }

  export function findCoverImage(eventProfile: EventProfile): string | null {
    const profilePhotosComponent = eventProfile.findComponentByTypeId<ProfilePhotosComponent>(
        ProfilePhotosComponent.TYPE_ID,
    );

    if(isNullOrUndefined(profilePhotosComponent)) {
      return null;
    }

    return profilePhotosComponent.portfolio.photos[0]?.downloadUrl ?? null;
  }

  export function findCoverImageLod0(eventProfile: EventProfile): string | null {
    const profilePhotosComponent = eventProfile.findComponentByTypeId<ProfilePhotosComponent>(
        ProfilePhotosComponent.TYPE_ID,
    );

    if(isNullOrUndefined(profilePhotosComponent)) {
      return null;
    }

    return profilePhotosComponent.portfolio.photos[0]?.lods[0]?.downloadUrl ?? null;
  }

  export function findCoverImageStoragePath(eventProfile: EventProfile): string | null {
    const profilePhotosComponent = eventProfile.findComponentByTypeId<ProfilePhotosComponent>(
        ProfilePhotosComponent.TYPE_ID,
    );

    if(isNullOrUndefined(profilePhotosComponent)) {
      return null;
    }

    return profilePhotosComponent.portfolio.photos[0]?.storagePath ?? null;
  }

  export function findLocationDisplayMode(eventProfile: EventProfile): PlaceDisplayMode | null {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(LocationComponent.TYPE_ID);
    return locationComponent?.displayMode ?? null;
  }

  export function findLocalityId(eventProfile: EventProfile): string | null {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID,
    );
    return locationComponent?.mapLocation?.address?.locality?.uniqueId ?? null;
  }

  export function findLocationName(eventProfile: EventProfile): LocalizedString | null {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID,
    );
    return locationComponent?.names ?? null;
  }

  export function findAddress(eventProfile: EventProfile): LocalizedString | null {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID,
    );
    return locationComponent?.mapLocation?.address?.formattedAddresses ?? null;
  }

  export function findCity(eventProfile: EventProfile): LocalizedString | null {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID,
    );
    return locationComponent?.mapLocation?.address?.locality?.names ?? null;
  }

  export function findGeoCoordinates(eventProfile: EventProfile): null | {
    latitude: number,
    longitude: number,
  } {
    const locationComponent = eventProfile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID,
    );
    return locationComponent?.mapLocation?.coordinates ?? null;
  }

  export function findClassification(eventProfile: EventProfile): EventCategory[] | null {
    const classificationComponent = eventProfile.findComponentByTypeId<EventClassificationComponent>(
        EventClassificationComponent.TYPE_ID,
    );

    return classificationComponent?.classification ?? null;
  }

  export function findEventPriority(eventProfile: EventProfile, partnerAppId?: string | null): EventPriority | null {
    return findEventClassificationComponent(eventProfile)
        ?.getPartnerPriorityOrDefault(partnerAppId) ?? null;
  }

  export function findFeaturedFlag(eventProfile: EventProfile, partnerAppId?: string | null): boolean | null {
    return findEventPriority(eventProfile, partnerAppId)?.isFeatured ?? null;
  }

  export function findFeaturedScore(eventProfile: EventProfile, partnerAppId?: string | null): number | null {
    return findEventPriority(eventProfile, partnerAppId)?.featuredScore ?? null;
  }

  export function findEventClassificationComponent(eventProfile: EventProfile): EventClassificationComponent | null {
    return eventProfile.findComponentByTypeId<EventClassificationComponent>(
        EventClassificationComponent.TYPE_ID,
    );
  }

  export function getEventDateFormat(eventProfile: EventProfile): EventDateFormat {
    const component = eventProfile.findComponentByTypeId<DateFormatComponent>(
        DateFormatComponent.TYPE_ID,
    );

    return component?.format ?? EventDateFormat.DateTime;
  }

  export function getPassEventOverrideLink(eventProfile: EventProfile): string | null {
    const component = eventProfile.findComponentByTypeId<PassEventOverrideIntentComponent>(
        PassEventOverrideIntentComponent.TYPE_ID,
    );

    return component?.solver ?? null;
  }

  export function isSeated(eventProfile: EventProfile): boolean {
    return eventProfile.hasComponentByTypeId(SeatingSettingsComponent.TYPE_ID);
  }

  export function getRemainingTicketQuantityWithoutSeating(
      event: EventProfile,
      eventTickets: EventTicket[]): Cardinal | null {
    const component = event.findComponentByTypeId<TicketTemplateComponent>(TicketTemplateComponent.TYPE_ID);
    if(component == null) {
      return null;
    }

    let totalQuantity: number = 0;
    for(const ticket of component.tickets) {
      if(ticket.quantity.isInfinite) {
        return Cardinal.infinity;
      }

      totalQuantity += ticket.quantity.value;
    }

    for(const ticket of eventTickets) {
      if(isNotNullOrUndefined(ticket.serialNumber) && (
        ticket.status === EventTicketStatus.Live ||
          ticket.status === EventTicketStatus.Scanned
      )) {
        totalQuantity--;
      }
    }

    return Cardinal.finite(totalQuantity);
  }

  export function getRemainingTicketQuantityWithSeating(
      seatingLayout: SeatingLayout,
      eventSeatingState: EventSeatingState,
      eventSeatSelectionLock: EventSeatSelectionLock | null): Cardinal {
    const seatingStateHelper = new SeatingStateHelper(eventSeatingState);

    const seats = findAllSeatsOfSeatingLayout(seatingLayout);

    let freeSeatAmount = 0;
    const freeSeatIds: Set<number> = new Set();
    for(const seat of seats) {
      const seatInfo = seatingStateHelper.getSeatInfo(seat.uniqueId);

      if(seat.tickets.length > 0 &&
        seatInfo.state === SeatState.Free) {
        freeSeatAmount++;
        freeSeatIds.add(seat.uniqueId);
      }
    }

    if(eventSeatSelectionLock == null) {
      return Cardinal.finite(freeSeatAmount);
    }

    const lockedSeatIds = new Set(eventSeatSelectionLock.entries
        .filter((seatSelectionEntry) => !seatSelectionEntry.isExpired())
        .flatMap((seatSelectionEntry) => seatSelectionEntry.seats));

    const lockedFreeSeatAmount = Array.from(lockedSeatIds)
        .filter((seatId) => freeSeatIds.has(seatId))
        .length;

    freeSeatAmount -= lockedFreeSeatAmount;

    return Cardinal.finite(freeSeatAmount);
  }

  export function getThirdPartyEventUrl(eventProfile: EventProfile): string | null {
    const component = eventProfile.findComponentByTypeId<ThirdPartyEventComponent>(
        ThirdPartyEventComponent.TYPE_ID,
    );

    return component?.url ?? null;
  }

  export function getFreeEventUrl(eventProfile: EventProfile): string | null {
    const component = eventProfile.findComponentByTypeId<ThirdPartyEventComponent>(
        ThirdPartyEventComponent.TYPE_ID,
    );

    return component?.freeEventUrl ?? null;
  }
}
